<script setup lang="ts">
// "All games" mega menu in the desktop category bar: platforms, the top
// genres from `GET /products/filters`, price buckets and the gift-card
// search. Opens on hover or click, closes on outside click, Escape and
// navigation. Genre counts are re-fetched on open once they are older than
// GENRES_TTL_MS, so they track the catalog the sync keeps changing.
import { computed, onBeforeUnmount, ref } from 'vue';
import { RouterLink, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { ChevronDown, CreditCard, LayoutGrid } from 'lucide-vue-next';
import { api } from '../api/client';
import { PLATFORMS, platformLink } from '../data/platforms';
import { PRICE_RANGES, priceRangeLink } from '../data/price-ranges';

interface Facet {
  name: string;
  count: number;
}

const { t } = useI18n();
const router = useRouter();

const open = ref(false);
const root = ref<HTMLElement | null>(null);
const genres = ref<Facet[]>([]);
let genresLoadedAt = 0;
let hoverTimer: number | undefined;

const GENRE_LIMIT = 12;
// Short enough that a count never trails the catalog page by more than a
// minute of sync; long enough that hovering in and out does not refetch.
const GENRES_TTL_MS = 60_000;
const topGenres = computed(() => genres.value.filter((g) => g.name && g.name !== 'Other').slice(0, GENRE_LIMIT));

async function loadGenres() {
  if (Date.now() - genresLoadedAt < GENRES_TTL_MS) return;
  genresLoadedAt = Date.now();
  try {
    const { data } = await api.get<{ genres: Facet[] }>('/products/filters');
    genres.value = data.genres ?? [];
  } catch {
    genresLoadedAt = 0; // retry on the next open
  }
}

function show() {
  window.clearTimeout(hoverTimer);
  open.value = true;
  void loadGenres();
}
function hide() {
  window.clearTimeout(hoverTimer);
  hoverTimer = window.setTimeout(() => (open.value = false), 120);
}
function toggle() {
  if (open.value) {
    open.value = false;
  } else {
    show();
  }
}
function onOutside(e: MouseEvent) {
  if (root.value && !root.value.contains(e.target as Node)) open.value = false;
}
function onKey(e: KeyboardEvent) {
  if (e.key === 'Escape') open.value = false;
}
document.addEventListener('click', onOutside);
document.addEventListener('keydown', onKey);
const stopRoute = router.afterEach(() => (open.value = false));
onBeforeUnmount(() => {
  document.removeEventListener('click', onOutside);
  document.removeEventListener('keydown', onKey);
  stopRoute();
  window.clearTimeout(hoverTimer);
});

const heading = 'mb-2 font-display text-[12px] font-semibold text-text-mute';
const link = 'flex h-8 items-center gap-2 rounded-md px-2 text-[13px] text-text-dim transition-colors hover:bg-card hover:text-text';
</script>

<template>
  <div ref="root" class="relative" @mouseenter="show" @mouseleave="hide">
    <button
      type="button"
      :aria-expanded="open"
      aria-controls="category-menu"
      class="flex h-9 items-center gap-2 rounded-lg px-3 font-display text-[13px] font-semibold text-text transition-colors hover:bg-card"
      :class="open && 'bg-card'"
      @click.stop="toggle"
    >
      <LayoutGrid class="size-4" :stroke-width="2" />
      {{ t('nav.allGames') }}
      <ChevronDown class="size-3.5 text-text-mute transition-transform" :class="open && 'rotate-180'" :stroke-width="2" />
    </button>

    <div
      v-if="open"
      id="category-menu"
      class="absolute left-0 top-[calc(100%+6px)] z-50 grid w-[760px] grid-cols-[1fr_1.4fr_1fr] gap-6 rounded-xl border border-border bg-surface p-5 shadow-lift"
    >
      <section>
        <p :class="heading">{{ t('nav.platforms') }}</p>
        <ul>
          <li v-for="p in PLATFORMS" :key="p.name">
            <RouterLink :to="platformLink(p.facets)" :class="link">
              <img :src="p.icon" alt="" aria-hidden="true" class="size-4 brightness-0 invert opacity-80" />
              {{ p.name }}
            </RouterLink>
          </li>
        </ul>
      </section>

      <section>
        <p :class="heading">{{ t('nav.genres') }}</p>
        <ul v-if="topGenres.length" class="grid grid-cols-2">
          <li v-for="g in topGenres" :key="g.name">
            <RouterLink :to="`/catalog?genre=${encodeURIComponent(g.name)}`" :class="link">
              <span class="truncate">{{ g.name }}</span>
              <span class="ml-auto text-[11px] text-text-mute">{{ g.count }}</span>
            </RouterLink>
          </li>
        </ul>
        <p v-else class="px-2 text-[13px] text-text-mute">{{ t('nav.genresEmpty') }}</p>
      </section>

      <section class="flex flex-col">
        <p :class="heading">{{ t('nav.shopByPrice') }}</p>
        <ul>
          <li v-for="r in PRICE_RANGES" :key="r.key">
            <RouterLink :to="priceRangeLink(r)" :class="link">{{ t(`home.price.${r.key}`) }}</RouterLink>
          </li>
        </ul>
        <RouterLink
          to="/catalog?q=card"
          class="mt-auto flex h-10 items-center gap-2 rounded-lg bg-brand-gradient px-3 font-display text-[13px] font-semibold text-white shadow-glow transition-[filter] hover:brightness-110"
        >
          <CreditCard class="size-4" :stroke-width="2" />
          {{ t('nav.giftCards') }}
        </RouterLink>
      </section>
    </div>
  </div>
</template>
